import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Hero from "../components/home/Hero";
import { ContinueStrip } from "../components/home/ContinueStrip";
import VisaGrid from "../components/home/VisaGrid";
import { MobileCTA } from "../components/home/MobileCTA";
// Below-the-fold sections load after the hero and grid so the first paint ships less JavaScript.
const Trending = lazy(() => import("../components/home/Trending"));
const CostCalculator = lazy(() => import("../components/home/CostCalculator"));
const Guarantees = lazy(() => import("../components/home/Guarantees"));
const HowItWorks = lazy(() => import("../components/home/HomeExtras").then((m) => ({ default: m.HowItWorks })));
const HomeFaq = lazy(() => import("../components/home/HomeExtras").then((m) => ({ default: m.HomeFaq })));
const ClosingCTAs = lazy(() => import("../components/home/HomeExtras").then((m) => ({ default: m.ClosingCTAs })));
import { IntentChips, INTENT_STYLE } from "../components/home/IntentHome";
import { getEligDraft } from "../lib/portal";

const KEY = "vl_intent";
const DEST_KEY = "vl_dest";
const INTENT_IDS = Object.keys(INTENT_STYLE);

export default function HomeCompact() {
  const [params, setParams] = useSearchParams();
  const draft = getEligDraft();
  const fromCheck = INTENT_IDS.includes(draft?.purpose) ? draft.purpose : null;
  const cat = params.get("cat") || localStorage.getItem(KEY) || fromCheck || "tourist";
  const dest = params.get("dest") ?? localStorage.getItem(DEST_KEY) ?? "";
  const pick = (id) => { localStorage.setItem(KEY, id); const p = new URLSearchParams(params); p.set("cat", id); setParams(p, { replace: true }); };
  const backToCards = () => {
    const row = document.querySelector('[data-testid="destinations-row"]');
    if (!row) return;
    const top = row.getBoundingClientRect().top + window.scrollY - 136;
    if (window.scrollY > top + 40 || row.getBoundingClientRect().top > window.innerHeight * 0.6) window.scrollTo({ top, behavior: "smooth" });
  };
  const pickSticky = (id) => { pick(id); requestAnimationFrame(() => setTimeout(backToCards, 60)); };
  const reset = () => { localStorage.removeItem(DEST_KEY); localStorage.setItem(KEY, "all"); const p = new URLSearchParams(params); p.set("dest", ""); p.set("cat", "all"); setParams(p, { replace: true }); };
  const cardRef = useRef(null);
  const [pinned, setPinned] = useState(false);
  useEffect(() => {
    let raf = 0;
    const check = () => {
      raf = 0;
      const c = cardRef.current?.getBoundingClientRect();
      setPinned(!!c && c.bottom < 72);
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(check); };
    check();
    window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => { window.removeEventListener("scroll", on); window.removeEventListener("resize", on); cancelAnimationFrame(raf); };
  }, []);
  return (
    <>
      <Hero persona="applicant" news={false} layout="search" />
      <ContinueStrip />
      <div ref={cardRef} className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"><IntentChips cat={cat} onPick={pick} inHero /></div>
      <AnimatePresence>
        {pinned && (
          <motion.div key="intent-pin" initial={{ y: -16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -16, opacity: 0 }} transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }} className="fixed inset-x-0 top-[72px] z-40" data-testid="intent-sticky">
            <IntentChips cat={cat} onPick={pickSticky} onReset={dest || cat !== "all" ? reset : null} />
          </motion.div>
        )}
      </AnimatePresence>
      <VisaGrid compact cat={cat} dest={dest} />
      <Suspense fallback={<div className="min-h-[60vh]" aria-hidden="true" />}>
        {cat === "tourist" && <Trending />}
        <HowItWorks compact />
        <Guarantees who="applicant" />
        {["tourist", "business", "student", "work"].includes(cat) && <CostCalculator />}
        <HomeFaq />
        <ClosingCTAs />
      </Suspense>
      <MobileCTA />
    </>
  );
}
